/**
 * messagesSlice — per-conversation messages + streaming state.
 *
 * Indexed by `conversationId` so switching the sidebar entry is O(1) and
 * each conversation's stream state stays isolated (no cross-talk when
 * the BE multiplexes queries).
 *
 * Streaming semantics:
 *   - `appendStreamChunk` is the hot path called per WS `text` event;
 *     mutates the message body in-place via immutable update.
 *   - `appendMessage` is for non-stream events (`answer`, `result`,
 *     `action_proposal`, ...) — deduplicated by message id, so an
 *     idempotent `action_proposal` arriving twice doesn't double-render.
 */

import type { StateCreator } from "zustand";
import type { ChatStore } from "./chatStore";
import type { MessageView } from "./types";

export interface StreamState {
  /** Active query id currently streaming into this conversation. */
  queryId: string | null;
  /** Plain "in flight" flag — used by Composer to swap send → stop button. */
  isStreaming: boolean;
  /** Cancellation sent; awaiting a terminal server event. */
  stopRequested?: boolean;
  /** Localized cancellation status, separate from tool progress copy. */
  cancellationLabel?: string;
  /** Surface status line ("Searching with HYBRID…"). */
  status?: string;
  /**
   * Latest human-readable reasoning topic — derived from the most recent
   * `reasoning_hop` WS event (e.g. "Sto leggendo le email…"). Drives the
   * `<StreamingIndicator>` blob-tier label. Cleared on stream completion.
   */
  currentReasoningLabel?: string;
  /**
   * First token of the answer text has landed. The `<ThinkingChain>` reads
   * this to morph its header from "sta pensando" → "sta scrivendo" and
   * collapse the hop list into a thin progress bar. Cleared on stream
   * completion alongside `isStreaming` / `queryId`.
   */
  isAnswering?: boolean;
}

export interface MessagesSlice {
  messagesByConversation: Record<string, MessageView[]>;
  streamingByConversation: Record<string, StreamState>;

  appendMessageTo: (conversationId: string, m: MessageView) => void;
  appendStreamChunk: (
    conversationId: string,
    messageId: string,
    delta: string,
    role?: MessageView["role"],
  ) => void;
  patchMessageIn: (
    conversationId: string,
    messageId: string,
    partial: Partial<MessageView>,
  ) => void;
  setStreamingFor: (conversationId: string, state: Partial<StreamState>) => void;
  hydrateMessagesFor: (conversationId: string, list: MessageView[]) => void;
  clearMessagesFor: (conversationId: string) => void;
}

function defaultStream(): StreamState {
  return { queryId: null, isStreaming: false };
}

export const createMessagesSlice: StateCreator<ChatStore, [], [], MessagesSlice> = (set) => ({
  messagesByConversation: {},
  streamingByConversation: {},

  appendMessageTo: (conversationId, m) =>
    set((state) => {
      const existing = state.messagesByConversation[conversationId] ?? [];
      // Dedup by id — idempotent insert (e.g. retried action_proposal).
      if (existing.some((x) => x.id === m.id)) {
        return {
          messagesByConversation: {
            ...state.messagesByConversation,
            [conversationId]: existing.map((x) => (x.id === m.id ? { ...x, ...m } : x)),
          },
        };
      }
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: [...existing, m],
        },
      };
    }),

  appendStreamChunk: (conversationId, messageId, delta, role = "agent") =>
    set((state) => {
      const existing = state.messagesByConversation[conversationId] ?? [];
      const target = existing.find((m) => m.id === messageId);
      let next: MessageView[];
      if (target) {
        next = existing.map((m) =>
          m.id === messageId ? { ...m, content: m.content + delta, status: "streaming" } : m,
        );
      } else {
        next = [
          ...existing,
          {
            id: messageId,
            conversationId,
            role,
            content: delta,
            timestamp: new Date().toISOString(),
            status: "streaming",
          },
        ];
      }
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: next,
        },
      };
    }),

  patchMessageIn: (conversationId, messageId, partial) =>
    set((state) => {
      const existing = state.messagesByConversation[conversationId] ?? [];
      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: existing.map((m) => (m.id === messageId ? { ...m, ...partial } : m)),
        },
      };
    }),

  setStreamingFor: (conversationId, partial) =>
    set((state) => ({
      streamingByConversation: {
        ...state.streamingByConversation,
        [conversationId]: {
          ...(state.streamingByConversation[conversationId] ?? defaultStream()),
          ...partial,
          ...(partial.isStreaming === false ? { stopRequested: false, cancellationLabel: undefined } : {}),
        },
      },
    })),

  hydrateMessagesFor: (conversationId, list) =>
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: list,
      },
    })),

  clearMessagesFor: (conversationId) =>
    set((state) => {
      const { [conversationId]: _, ...rest } = state.messagesByConversation;
      const { [conversationId]: _s, ...restStream } = state.streamingByConversation;
      return {
        messagesByConversation: rest,
        streamingByConversation: restStream,
      };
    }),
});
